import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Banknote, CalendarClock, CreditCard, ReceiptText } from "lucide-react";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDateBR } from "@/lib/date-utils";
import { AppointmentDetailsDialog } from "@/components/calendar/AppointmentDetailsDialog";
import { SaleDetailModal } from "@/components/erp/pos/SaleDetailModal";
import { AppointmentStatusBadge, AppointmentTypePill } from "@/components/patients/appointment-ui-badges";
import { APPOINTMENT_STATUS_LABEL, PAYMENT_METHOD_LABELS, formatCurrency } from "@/components/patients/appointment-ui-meta";
import type { FinancialOpenAppointmentRow, FinancialPaymentHistoryRow, FinancialReceivableRow, FinancialSale, FinancialSummary } from "@/hooks/useFinancialSummary";

function OriginCell({ origin, t }: { origin: FinancialReceivableRow["origin"]; t: (key: string, vars?: Record<string, unknown>) => string }) {
  if (!origin) return <span className="text-muted-foreground">{t("financialTab.originNone")}</span>;
  return (
    <span>
      {t("financialTab.originStayPlan", {
        module: t(origin.module === "hotel" ? "financialTab.moduleHotel" : "financialTab.moduleDaycare"),
        plan: origin.planName,
      })}
    </span>
  );
}

const SummaryCard = ({
  title,
  value,
  detail,
  icon: Icon,
}: {
  title: string;
  value: string;
  detail?: string;
  icon: typeof ReceiptText;
}) => (
  <div className="rounded-lg border border-border bg-white p-4">
    <div className="flex items-start justify-between gap-4">
      <div className="space-y-1">
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="text-2xl font-semibold text-foreground">{value}</p>
        {detail ? <p className="text-xs text-muted-foreground">{detail}</p> : null}
      </div>
      <div className="rounded-full bg-muted p-2 text-muted-foreground">
        <Icon className="h-4 w-4" />
      </div>
    </div>
  </div>
);

export function FinancialTabContent({
  summary,
  openAppointments,
  paymentHistory,
  sales,
  openReceivables,
  isLoading,
  error,
  emptyMessage,
  showPetColumn = false,
}: {
  summary?: FinancialSummary;
  openAppointments?: FinancialOpenAppointmentRow[];
  paymentHistory?: FinancialPaymentHistoryRow[];
  sales?: FinancialSale[];
  openReceivables?: FinancialReceivableRow[];
  isLoading: boolean;
  error: unknown;
  emptyMessage: string;
  showPetColumn?: boolean;
}) {
  const { t } = useTranslation("patients");
  const [selectedSaleId, setSelectedSaleId] = useState<string | null>(null);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState<string | null>(null);

  const resolvedSummary = {
    totalOpen: Number(summary?.totalOpen ?? 0),
    openCount: Number(summary?.openCount ?? 0),
    totalReceived: Number(summary?.totalReceived ?? 0),
    lastReceivedAt: summary?.lastReceivedAt ?? null,
    openAppointmentsCount: Number(summary?.openAppointmentsCount ?? 0),
    openAppointmentsTotal: Number(summary?.openAppointmentsTotal ?? 0),
  };
  const resolvedOpenAppointments = openAppointments ?? [];
  const resolvedPaymentHistory = paymentHistory ?? [];
  const resolvedOpenReceivables = openReceivables ?? [];

  const saleStatusMap = useMemo(() => {
    const map = new Map<string, { label: string; amount?: number; className: string }>();

    for (const sale of sales ?? []) {
      const receivableAmount = sale.payments
        .filter((payment) => payment.method === "A_RECEBER" && !payment.receivedAt)
        .reduce((sum, payment) => sum + payment.amount, 0);

      if (sale.status === "CANCELADA") {
        map.set(sale.id, { label: t("financialTab.saleStatus.cancelled"), className: "border-red-200 text-red-700 bg-red-100" });
      } else if (sale.status === "DEVOLVIDA") {
        map.set(sale.id, { label: t("financialTab.saleStatus.returned"), className: "border-slate-300 text-slate-700 bg-slate-100" });
      } else if (receivableAmount > 0) {
        map.set(sale.id, { label: t("financialTab.saleStatus.receivable"), amount: receivableAmount, className: "border-amber-200 text-amber-700 bg-amber-100" });
      } else {
        map.set(sale.id, { label: t("financialTab.saleStatus.paid"), className: "border-emerald-200 text-emerald-700 bg-emerald-100" });
      }
    }

    return map;
  }, [sales, t]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return <div className="rounded-lg border border-border bg-muted/20 px-4 py-6 text-sm text-muted-foreground">{t("financialTab.loadError")}</div>;
  }

  if (resolvedOpenAppointments.length === 0 && resolvedPaymentHistory.length === 0 && resolvedOpenReceivables.length === 0) {
    return <div className="rounded-lg border border-border bg-muted/20 px-4 py-6 text-sm text-muted-foreground">{emptyMessage}</div>;
  }

  return (
    <>
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <SummaryCard title={t("financialTab.summary.receivable")} value={formatCurrency(resolvedSummary.totalOpen)} detail={t("financialTab.summary.pendingInstallments", { count: resolvedSummary.openCount })} icon={Banknote} />
          <SummaryCard title={t("financialTab.summary.receivedTotal")} value={formatCurrency(resolvedSummary.totalReceived)} icon={CreditCard} />
          <SummaryCard title={t("financialTab.summary.openAppointments")} value={formatCurrency(resolvedSummary.openAppointmentsTotal)} detail={t("financialTab.summary.openAppointmentsCount", { count: resolvedSummary.openAppointmentsCount })} icon={ReceiptText} />
          <SummaryCard title={t("financialTab.summary.lastReceipt")} value={resolvedSummary.lastReceivedAt ? formatDateBR(resolvedSummary.lastReceivedAt) : t("financialTab.summary.notReceived")} icon={CalendarClock} />
        </div>

        <section className="space-y-3">
          <div>
            <h4 className="text-sm font-semibold text-foreground">{t("financialTab.sections.openReceivables")}</h4>
          </div>
          {resolvedOpenReceivables.length === 0 ? (
            <div className="rounded-lg border border-border bg-muted/20 px-4 py-5 text-sm text-muted-foreground">{t("financialTab.empty.openReceivables")}</div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border bg-white">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("financialTab.columns.date")}</TableHead>
                    {showPetColumn ? <TableHead>{t("financialTab.columns.patient")}</TableHead> : null}
                    <TableHead>{t("financialTab.columns.description")}</TableHead>
                    <TableHead>{t("financialTab.columns.origin")}</TableHead>
                    <TableHead className="text-right">{t("financialTab.columns.amountOpen")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resolvedOpenReceivables.map((row) => (
                    <TableRow key={row.saleId} className="cursor-pointer hover:bg-secondary" onClick={() => setSelectedSaleId(row.saleId)}>
                      <TableCell>{row.saleDate ? formatDateBR(row.saleDate) : "—"}</TableCell>
                      {showPetColumn ? <TableCell>{row.petName ?? "—"}</TableCell> : null}
                      <TableCell className="max-w-[280px] truncate">{row.description}</TableCell>
                      <TableCell><OriginCell origin={row.origin} t={t} /></TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(row.amountOpen)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>

        <section className="space-y-3">
          <div>
            <h4 className="text-sm font-semibold text-foreground">{t("financialTab.sections.open")}</h4>
          </div>
          {resolvedOpenAppointments.length === 0 ? (
            <div className="rounded-lg border border-border bg-muted/20 px-4 py-5 text-sm text-muted-foreground">{t("financialTab.empty.openAppointments")}</div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border bg-white">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("financialTab.columns.date")}</TableHead>
                    {showPetColumn ? <TableHead>{t("financialTab.columns.patient")}</TableHead> : null}
                    <TableHead>{t("financialTab.columns.type")}</TableHead>
                    <TableHead>{t("financialTab.columns.status")}</TableHead>
                    <TableHead className="text-right">{t("financialTab.columns.estimatedValue")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resolvedOpenAppointments.map((row) => (
                    <TableRow key={row.appointmentId} className="cursor-pointer bg-white" onClick={() => setSelectedAppointmentId(row.appointmentId)}>
                      <TableCell>{row.startsAt ? formatDateBR(row.startsAt) : "—"}</TableCell>
                      {showPetColumn ? <TableCell>{row.petName ?? "—"}</TableCell> : null}
                      <TableCell>{(row as { clinic_appointment_type?: { name: string; color: string; icon: string } | null }).clinic_appointment_type ? <AppointmentTypePill clinicType={(row as { clinic_appointment_type?: { name: string; color: string; icon: string } | null }).clinic_appointment_type} /> : "—"}</TableCell>
                      <TableCell><AppointmentStatusBadge statusKey={row.status ?? undefined} statusLabel={row.status ? (APPOINTMENT_STATUS_LABEL[row.status] ?? row.status) : "—"} /></TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(row.estimatedTotal)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>

        <section className="space-y-3">
          <div>
            <h4 className="text-sm font-semibold text-foreground">{t("financialTab.sections.paymentHistory")}</h4>
          </div>
          {resolvedPaymentHistory.length === 0 ? (
            <div className="rounded-lg border border-border bg-muted/20 px-4 py-5 text-sm text-muted-foreground">{t("financialTab.empty.payments")}</div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-border bg-white">
              <Table className="[&_td]:px-3 [&_th]:px-3">
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("financialTab.columns.number")}</TableHead>
                    {showPetColumn ? <TableHead>{t("financialTab.columns.patient")}</TableHead> : null}
                    <TableHead>{t("financialTab.columns.payment")}</TableHead>
                    <TableHead>{t("financialTab.columns.method")}</TableHead>
                    <TableHead>{t("financialTab.columns.status")}</TableHead>
                    <TableHead>{t("financialTab.columns.description")}</TableHead>
                    <TableHead>{t("financialTab.columns.reference")}</TableHead>
                    <TableHead className="text-right">{t("financialTab.columns.value")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {resolvedPaymentHistory.map((row) => {
                    const saleStatus = saleStatusMap.get(row.saleId);
                    return (
                      <TableRow key={row.paymentId} className="cursor-pointer hover:bg-secondary" onClick={() => setSelectedSaleId(row.saleId)}>
                        <TableCell className="font-mono">{row.saleNumber}</TableCell>
                        {showPetColumn ? <TableCell>{row.petName ?? "—"}</TableCell> : null}
                        <TableCell>{row.paymentDate ? formatDateBR(row.paymentDate) : "—"}</TableCell>
                        <TableCell>{PAYMENT_METHOD_LABELS[row.method] ?? row.method}</TableCell>
                        <TableCell>
                          {saleStatus ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <Badge variant="outline" className={saleStatus.className}>{saleStatus.label}</Badge>
                              {saleStatus.amount != null && saleStatus.amount > 0 ? <span className="text-xs text-muted-foreground">{formatCurrency(saleStatus.amount)}</span> : null}
                            </div>
                          ) : "—"}
                        </TableCell>
                        <TableCell className="max-w-[160px] truncate" title={row.description}>{row.description}</TableCell>
                        <TableCell className="max-w-[120px] truncate" title={row.reference ?? undefined}>{row.reference ?? "—"}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(row.amount)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      </div>
      <SaleDetailModal saleId={selectedSaleId} open={Boolean(selectedSaleId)} onOpenChange={(open) => { if (!open) setSelectedSaleId(null); }} />
      <AppointmentDetailsDialog appointmentId={selectedAppointmentId ?? ""} open={Boolean(selectedAppointmentId)} onOpenChange={(open) => { if (!open) setSelectedAppointmentId(null); }} />
    </>
  );
}
